import { breadcrumbSchema } from "@/lib/schema";
import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/Layout";
import { Section, SectionEyebrow } from "@/components/site/Section";
import { CTABanner } from "@/components/site/CTABanner";

export const Route = createFileRoute("/agents")({
  head: () => ({
    meta: [
      { title: "Real Estate Teams Hiring - Find Your Fit at KCN" },
      {
        name: "description",
        content:
          "Real estate teams hiring top producers, mom agents, and values-aligned newcomers. See how the Kristan Cole Network fits the season you are in right now.",
      },
      { property: "og:title", content: "For Agents - Kristan Cole Network" },
      {
        property: "og:description",
        content:
          "Three kinds of agents thrive on this team. See which one is you.",
      },
      { property: "og:url", content: "https://joinkcn.com/agents" },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/agents" }],
    scripts: [breadcrumbSchema([{ name: "Agents", path: "/agents" }])],
  }),
  component: AgentsPage,
});

const personas = [
  {
    tag: "Legacy Builder",
    name: "The Purpose-Driven Mom Agent",
    bio: "5-10 years in the business. Mid-producer. School-aged kids. Refuses to grow at the cost of her family.",
    quote:
      "I want to lead like a CEO without losing my soul. Faith and family matter as much as success - and I need systems that let me do both.",
    fit: [
      "You're done choosing between dinner at home and your next deal",
      "You want community and mentorship, not boss-babe theatrics",
      "You're building for your kids as much as for yourself",
    ],
  },
  {
    tag: "Future CEO",
    name: "The Burned-Out Top Producer",
    bio: "7-12 years. $250K-$400K GCI. Drowning in deals. Allergic to most 'teams' you've seen.",
    quote:
      "I'm the bottleneck. I know I need leverage - I just don't trust most teams to build it the right way.",
    fit: [
      "You've outgrown solo, but no team has earned your trust",
      "You want a playbook, not a pep talk",
      "You're ready to build wealth, not just earn income",
    ],
  },
  {
    tag: "Rising Pro",
    name: "The Values-Aligned Newcomer",
    bio: "New to real estate. Coming from education, ministry, nonprofit, or nursing. Strong values. Spiritually grounded.",
    quote:
      "I prayed for a place that wasn't all hustle. I need mentorship that lines up with what I actually believe.",
    fit: [
      "You want training that's structured, smart, and principled",
      "You're not interested in 'sales bro' culture",
      "You want a long-term path - coach, leader, or top agent",
    ],
  },
];

function AgentsPage() {
  return (
    <SiteLayout>
      <section className="pt-36 pb-14 border-b border-border">
        <div className="container-edge grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow mb-6">For Agents</p>
            <h1 className="headline-xl">
              Three kinds of agents
              <br />
              <span className="serif-italic text-primary">thrive here.</span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-foreground/90 text-lg leading-relaxed">
              See which one is you - and decide if it's time to have a
              conversation.
            </p>
          </div>
        </div>
      </section>

      {personas.map((p, i) => (
        <Section key={p.tag} tone={i % 2 === 0 ? "dark" : "darker"}>
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="eyebrow-on-dark text-xs uppercase tracking-[0.25em] mb-4">
                {p.tag}
              </p>
              <h2 className="headline-md mb-6">{p.name}</h2>
              <p className="text-foreground/90 leading-relaxed">{p.bio}</p>
              <p className="mt-10 font-display italic text-2xl text-foreground/90 leading-snug border-l-2 border-primary pl-6">
                "{p.quote}"
              </p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="eyebrow mb-6">You're a fit if…</p>
              <ul className="divide-y divide-border border-y border-border">
                {p.fit.map((f) => (
                  <li
                    key={f}
                    className="py-5 flex items-start gap-4 text-lg text-foreground/85"
                  >
                    <span className="text-primary text-xs mt-2">◆</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      ))}

      <CTABanner
        cta="roundtable"
        eyebrow="See yourself in one of these?"
        title="Then the next step is a seat at the Round Table."
        body="Request an invitation and see how Kristan coaches her team."
      />
    </SiteLayout>
  );
}
